import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useSnackbar } from 'notistack';
import FulfilmentLocationForm from '../FulfilmentLocationForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import translations from './translations';

const CreateFulfilmentLocationDrawer = ({ open, onClose, onSuccess }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef(null);

  /**
   * Handle creating a fulfilment location.
   * @param {object} formData
   * @returns {Promise<never>}
   */
  const handleCreateFulfilmentLocation = async (formData = {}) => {
    try {
      const { name, address: newAddress } = formData;

      const locationResponse = await fulfilmentApi.createFulfilmentLocation({
        name,
        address_id: newAddress?.id,
      });
      const { data = {} } = locationResponse;

      if (onSuccess) {
        onSuccess(data);
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.fulfilmentLocationCreated), {
        variant: 'success',
      });
    } catch (error) {
      const { message, code } = error?.response?.data ?? {};
      if (message && message.toLowerCase() === 'conflict' && code === 400) {
        formRef.current.setError(
          'address',
          'addressAlreadyLinkedToLocation',
          'This address has already been used',
        );
      } else {
        enqueueSnackbar(intl.formatMessage(translations.feedback.fulfilmentLocationNotCreated), {
          variant: 'error',
        });
      }
    }
  };

  return (
    <FormDrawer
      isOpen={open}
      title={intl.formatMessage(translations.title)}
      drawerWidth="400px"
      onClose={onClose}
    >
      <FulfilmentLocationForm
        inline={false}
        onSubmit={handleCreateFulfilmentLocation}
        onCancel={onClose}
        formRef={formRef}
      />
    </FormDrawer>
  );
};

CreateFulfilmentLocationDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

CreateFulfilmentLocationDrawer.defaultProps = {
  open: false,
  onClose: undefined,
  onSuccess: undefined,
};

export default CreateFulfilmentLocationDrawer;
